import type { ScreenConfig, ThemeVariant } from "../../types";

/**
 * Offline package file parsing helpers used by ViewerExpressLegacyAdapter.
 *
 * Responsibilities:
 * - Read screen config (`screens/<screenId>.json`)
 * - Read theme variant metadata (`ambrosia.js`)
 * - Resolve reference annotations (`annotations.json`)
 * - Read glossary entries (`glossary.json`)
 *
 * Parsing model:
 * - `ambrosia.js` exposes a JSON-like payload via an assignment prefix (`ambrosia =`)
 *   that is stripped before parsing; annotations and glossary are plain JSON files.
 * - Each helper parses JSON, validates required shape, then returns typed data.
 */

export const fetchAmbrosiaJson = async (packagePath: string): Promise<Record<string, any>> => {
  const ambrosiaPath = `${packagePath.replace(/\/$/, "")}/ambrosia.js`;
  const res = await fetch(ambrosiaPath);
  if (!res.ok) {
    throw new Error(`Failed to fetch ambrosia.js from ${ambrosiaPath}`);
  }
  const text = await res.text();
  return JSON.parse(text.substring("ambrosia = ".length)) as Record<string, any>;
};

export const getScreenConfig = async (
  screenId: string,
  packagePath: string,
): Promise<ScreenConfig> => {
  const screenPath = `${packagePath}/screens/${screenId}.json`;
  const response = await fetch(screenPath);
  if (!response.ok) {
    throw new Error(`Screen config not found for "${screenId}"`);
  }

  const dataJson = await response.json();

  if (!dataJson?.stage) {
    throw new Error("Stage not found in the screen config");
  }

  return dataJson as ScreenConfig;
};

export const getThemeVariant = async (
  themeVariantId: string,
  packagePath: string,
): Promise<ThemeVariant> => {
  const dataJson = await fetchAmbrosiaJson(packagePath);

  const theme = dataJson?.selectedThemePayload?.themeVariants?.find(
    (item: any) => item.variantId === themeVariantId,
  );
  if (!theme) {
    throw new Error("Theme variant not found in the ambrosia.js");
  }

  return theme as ThemeVariant;
};

export const getAnnotation = async (referenceId: string, packagePath: string) => {
  const annotationPath = packagePath + `/annotations.json`;
  const response = await fetch(annotationPath);

  const dataJson = await response.json();

  if (!dataJson[referenceId]) {
    throw new Error(`ReferenceId "${referenceId}" not found in the annotations.json`);
  }

  return dataJson[referenceId];
};

export const getGlossary = async (packagePath: string) => {
  const glossaryPath = packagePath + `/glossary.json`;
  const response = await fetch(glossaryPath);

  const dataJson = await response.json();

  if (!dataJson || !dataJson.data || dataJson.data.length === 0) {
    return [];
  }

  return dataJson.data;
};
